"use client";

import { captureException } from "@sentry/nextjs";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  BattlefieldCard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div className="battlefield-bg-pattern relative min-h-screen overflow-hidden bg-gradient-to-br from-background via-background/95 to-muted/20">
          {/* Background effects */}
          <div className="absolute inset-0 overflow-hidden opacity-30">
            <div className="battlefield-grid absolute inset-0" />

            {/* Scanning lines effect */}
            <div className="absolute left-0 h-px w-full animate-pulse bg-gradient-to-r from-transparent via-destructive/50 to-transparent" />
          </div>

          <div className="container relative mx-auto flex min-h-screen items-center justify-center px-4">
            <BattlefieldCard className="battlefield-border w-full max-w-lg">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-10 w-10 text-destructive" />
                </div>
                <CardTitle className="font-heading text-3xl">
                  CRITICAL SYSTEM ERROR
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-center">
                <div className="space-y-3">
                  <p className="font-body text-lg text-muted-foreground">
                    The battlefield command network has encountered a critical
                    error and needs to restart.
                  </p>
                  <p className="font-body text-muted-foreground text-sm">
                    This is usually temporary and should resolve after reloading
                    the application.
                  </p>
                  {error.digest && (
                    <div className="rounded-lg bg-muted p-3">
                      <p className="font-mono text-muted-foreground text-xs">
                        Error ID: {error.digest}
                      </p>
                      <p className="font-mono text-muted-foreground text-xs">
                        {error.message}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <Button
                    className="battlefield-button w-full"
                    onClick={reset}
                    size="lg"
                  >
                    <RefreshCw className="mr-2 h-5 w-5" />
                    Restart Application
                  </Button>

                  <Button
                    className="w-full"
                    onClick={() => (window.location.href = "/")}
                    size="lg"
                    variant="outline"
                  >
                    <Home className="mr-2 h-5 w-5" />
                    Return to Base
                  </Button>
                </div>

                <div className="border-t pt-6">
                  <div className="space-y-2">
                    <p className="font-heading text-destructive text-xs uppercase tracking-wider">
                      [ SYSTEM STATUS: OFFLINE ]
                    </p>
                    <p className="font-body text-muted-foreground text-xs">
                      BF6 Loadouts Command Network
                    </p>
                    <p className="font-body text-muted-foreground text-xs">
                      Contact support if this error persists
                    </p>
                  </div>
                </div>
              </CardContent>
            </BattlefieldCard>
          </div>
        </div>
      </body>
    </html>
  );
}
